/* eslint-disable max-lines-per-function */

import { useCallback } from 'react';

import {
   MedicalServicePerformableByPractitionerSimple,
   TaxRate,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { Bean, ValidationRules } from '@dreryk/react-common';
import { InteractiveTable, InteractiveTableCellProps, useInteractiveTableContext } from '@dreryk/react-components';

import { invoiceCreateModalTranslationKeys } from '../../../translations';

import { InvoiceItemsTableBean } from './InvoiceItemsTable.types';
import {
   getMedicalServiceName,
   getTaxRate,
   mapMedicalServiceTaxRateCodeToInvoiceTaxRate,
   medicalServiceRowVisitor,
} from './InvoiceItemsTable.utils';
import { InvoiceItemsTableMedicalServiceNameCellTooltip } from './InvoiceItemsTableMedicalServiceNameCellTooltip';
import {
   medicalServiceItemRenderer,
   medicalServiceKeyExtractor,
   medicalServiceSearchCreator,
   medicalServiceTextInputValueExtractor,
} from './InvoiceItemsTableMedicalServiceNameCell.utils';

const VALIDATION_RULES: ValidationRules<Bean> = {
   required: true,
   maxLength: 200,
};

type InvoiceItemsTableMedicalServiceNameCellProps = InteractiveTableCellProps<
   string | MedicalServicePerformableByPractitionerSimple
> & {
   taxRates: TaxRate[];
   practitionerId?: PractitionerId;
};

export const InvoiceItemsTableMedicalServiceNameCell = (
   props: InvoiceItemsTableMedicalServiceNameCellProps
): JSX.Element => {
   const { defaultValue, index, name, taxRates, testId } = props;
   const { getCellValue, setCellValue } = useInteractiveTableContext<InvoiceItemsTableBean>();
   const medicalServiceName = getCellValue('name', index);
   const invoiceParameters = getCellValue('invoiceParameters', index);

   const handleSelectionChange = useCallback(
      (selectedItem: MedicalServicePerformableByPractitionerSimple | null): void => {
         if (selectedItem) {
            const taxRate = getTaxRate(taxRates, selectedItem.taxRateId);
            setCellValue('quantity', index, 1);
            setCellValue('taxRate', index, mapMedicalServiceTaxRateCodeToInvoiceTaxRate(taxRate.code));
            setCellValue('unit', index, 'SERVICE');
            setCellValue('unitPrice', index, selectedItem.price.asNumber() ?? null);
            setCellValue('invoiceParameters', index, {
               ...invoiceParameters,
               nameEditable: false,
               nameForInvoicing: selectedItem.nameForInvoicing ?? null,
               unitDefaultPrice: selectedItem.price.asNumber(),
            });
         }
      },
      [invoiceParameters, index, setCellValue, taxRates]
   );

   return medicalServiceRowVisitor({
      nameEditable: invoiceParameters.nameEditable,
      rowAddedManually: invoiceParameters.rowAddedManually,
      practitionerId: props.practitionerId,
   })({
      visitFetchedRow: () => (
         <InteractiveTable.TextViewCell
            defaultValue={getMedicalServiceName(defaultValue)}
            index={index}
            name={name}
            renderer={(value) => (
               <InvoiceItemsTableMedicalServiceNameCellTooltip
                  name={value}
                  nameForInvoicing={invoiceParameters.nameForInvoicing}
               />
            )}
            testId={testId}
         />
      ),
      visitManuallyAddedRow: (practitionerId?: PractitionerId) => (
         <InteractiveTable.AutosuggestCell<MedicalServicePerformableByPractitionerSimple>
            defaultValue={null}
            index={index}
            itemRenderer={medicalServiceItemRenderer}
            keyExtractor={medicalServiceKeyExtractor}
            name={name}
            placeholder={invoiceCreateModalTranslationKeys.table.name.placeholder}
            searchCreator={medicalServiceSearchCreator(practitionerId)}
            testId={testId}
            textInputValueExtractor={medicalServiceTextInputValueExtractor}
            validationRules={VALIDATION_RULES}
            onSelectionChange={handleSelectionChange}
         />
      ),
      visitManuallyAddedRowWithName: () => (
         <InteractiveTable.TextViewCell
            defaultValue={getMedicalServiceName(medicalServiceName)}
            index={index}
            name={name}
            renderer={(value) => (
               <InvoiceItemsTableMedicalServiceNameCellTooltip
                  name={value}
                  nameForInvoicing={invoiceParameters.nameForInvoicing}
               />
            )}
            testId={testId}
         />
      ),
   });
};
